<template>
  <div class="zac-root">
    <ctlHomeNav></ctlHomeNav>
    <v-toolbar dark color="secondary" dense class="zac-toolbar">
      <v-toolbar-title class="title">Reassign ZIPs to Sales Rep</v-toolbar-title>
      <v-divider vertical class="mx-2"></v-divider>
      <div class="zac-toolbar-field">
        <v-select dense hide-details solo-inverted flat
          :items="usStates" item-text="name" item-value="abbreviation"
          v-model="paras.state" label="State"></v-select>
      </div>
      <div class="zac-toolbar-field">
        <v-select dense hide-details solo-inverted flat
          :items="['Standard','GI']" v-model="paras.territoryTypeInMap" label="Type"></v-select>
      </div>
      <v-btn small color="primary" @click="onSubmit">Submit</v-btn>
      <v-spacer></v-spacer>
      <v-btn small outline class="ml-2" @click="onShowSaveTerrRepZipsRpt">Save Map</v-btn>
      <v-btn small outline class="ml-2" @click="showTerrRepZipsRpt = true">Change Summary</v-btn>
      <v-btn icon small class="ml-2" @click="sidebarOpen = !sidebarOpen"
        :title="sidebarOpen ? 'Hide sidebar' : 'Show sidebar'">
        <v-icon>{{ sidebarOpen ? 'chevron_right' : 'chevron_left' }}</v-icon>
      </v-btn>
      <v-btn icon small class="ml-1" @click="showInstructions = true" title="How to use">
        <v-icon>help_outline</v-icon>
      </v-btn>
    </v-toolbar>
    <ctlMapNewRepDialog v-model="showNewRepDialog" :colors="map_colors"
      :default-color="nextUnusedColor" @save="onAddNewRep"></ctlMapNewRepDialog>
    <v-dialog v-model="showTerrRepZipsRpt" width="800" height="600">
      <ctlTerrRepZipsRpt @onclose="showTerrRepZipsRpt=false"
        :state="paras.state"
        :tazTerrXRepsXZips="tazTerrXRepsXZips"></ctlTerrRepZipsRpt>
    </v-dialog>
    <v-dialog v-model="showSaveTerrRepZipsRpt" width="500" height="400">
      <ctlSaveTerrRepZipsRpt @onclose="showSaveTerrRepZipsRpt=false"
        :paras="paras"
        :tazTerrXRepsXZips="tazTerrXRepsXZips"
        :savedMapData="savedTerrRepZipsRpt"></ctlSaveTerrRepZipsRpt>
    </v-dialog>
    <v-dialog v-model="showInstructions" width="640">
      <v-card>
        <v-card-title class="headline">How to Reassign ZIPs to Sales Reps</v-card-title>
        <v-card-text>
          <ol class="zac-howto">
            <li>Pick a <b>State</b> and click <b>Submit</b> to load the map.</li>
            <li>Click any ZIP code polygon on the map &mdash; it will appear in the <b>Pending ZIPs</b> tray on the right. You can also paste a comma-separated ZIP list directly into that textarea.</li>
            <li>Pick a target Sales Rep from the <b>Reassign to</b> dropdown.</li>
            <li>Click <b>Reassign N ZIPs</b> &mdash; the selected ZIPs are repainted in the rep's color and the rep's ZIP count updates in the right panel.</li>
            <li>Click <b>Highlight on map</b> in the Pending tray to flash the staged ZIPs (or click any rep's <b>"N ZIPs"</b> link to see and highlight that rep's ZIPs).</li>
            <li>Click <b>+ New Rep</b> to simulate a new sales rep joining the company (Territory ID, Rep Name, Color).</li>
            <li>When you're done, click <b>Save Map</b> to keep the current state, or <b>Change Summary</b> to generate a list of reassignments you can email to Sales Admin.</li>
            <li>Use the <b>chevron</b> in the toolbar to hide the right panel and give the map more room.</li>
          </ol>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showInstructions = false">Got it</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <div class="zac-body">
      <div class="zac-map-area">
        <div v-if="hoveredFeature" class="zac-hoverbar">
          <span><b>ZIP</b> {{ hoveredFeature.zip }}</span>
          <span><b>Territory</b> {{ hoveredFeature.terr || '—' }}</span>
          <span><b>Rep</b> {{ hoveredFeature.rep || '—' }}</span>
          <span><b>12-mo Sales</b> ${{ hoveredFeature.salesAmt ? Number(hoveredFeature.salesAmt).toFixed(2) : '0.00' }}</span>
        </div>
        <div v-if="!hasMapData" class="zac-empty">Pick a state and click Submit to load the map.</div>
        <v-menu v-if="hasMapData" offset-y top :close-on-content-click="false">
          <v-btn slot="activator" fab small color="white" class="zac-legend-btn">
            <v-icon color="grey darken-1">place</v-icon>
          </v-btn>
          <v-card class="zac-legend">
            <v-card-title class="py-2 subheading"><b>Facility Marks</b></v-card-title>
            <v-card-text class="pa-2">
              <div v-for="t in territories" :key="t.id" class="d-flex align-center mb-1">
                <img :src="t.iconUrl" alt="" class="zac-legend-icon">
                <span class="caption ml-2">{{ t.id }} ({{ t.customerQty }})</span>
              </div>
              <div v-if="!territories || !territories.length" class="caption grey--text">No facility data.</div>
            </v-card-text>
          </v-card>
        </v-menu>
        <div id="zacDivmap" class="zac-map"></div>
      </div>
      <div class="zac-sidebar" :class="{ 'zac-sidebar--collapsed': !sidebarOpen }">
        <ctlMapPendingTray
          v-model="zipsSelected"
          :reps="tazTerrXRepsXZips"
          :selected-rep.sync="selectedRep"
          @assign="onAssignZips"
          @highlight-pending="onHighlightPending"></ctlMapPendingTray>
        <ctlMapRepList :reps="tazTerrXRepsXZips"
          @highlight="onHighlightRep"
          @add-new-rep="showNewRepDialog = true"></ctlMapRepList>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import gmapsInit from '@/services/gmaps'
import { collectMapBoundsCoordinates, getFeatureProperty } from '@/services/tools/mapBounds'
import { parseZips } from './parseZips'
import ctlHomeNav from '@/components/ctlHomeNav'
import ctlMapPendingTray from './ctlMapPendingTray'
import ctlMapRepList from './ctlMapRepList'
import ctlMapNewRepDialog from './ctlMapNewRepDialog'
import ctlTerrRepZipsRpt from '@/components/tools/ctlTerrRepZipsRpt'
import ctlSaveTerrRepZipsRpt from '@/components/tools/ctlSaveTerrRepZipsRpt'

export default {
  components: { ctlHomeNav, ctlMapPendingTray, ctlMapRepList, ctlMapNewRepDialog, ctlTerrRepZipsRpt, ctlSaveTerrRepZipsRpt },
  data: () => ({
    paras: { state: 'MI', territory: null, territoryTypeInMap: 'Standard' },
    locations: [],
    territories: [],
    usStates: [],
    zipsSelected: '',
    selectedRep: null,
    highlightedRepId: null,
    highlightTimer: null,
    showNewRepDialog: false,
    showTerrRepZipsRpt: false,
    showSaveTerrRepZipsRpt: false,
    showInstructions: false,
    sidebarOpen: true,
    hoveredFeature: null,
    tazTerrXRepsXZips: [],
    map_colors: [],
    zipGeoJsons: {},
    savedTerrRepZipsRpt: {},
  }),
  computed: {
    ...mapGetters('auth', ['userData']),
    id () { return this.$route.params.id ? this.$route.params.id : null },
    isNew () { return !this.id },
    hasMapData () { return !!(this.zipGeoJsons && this.zipGeoJsons.features && this.zipGeoJsons.features.length) },
    nextUnusedColor () {
      const used = new Set(this.tazTerrXRepsXZips.map(r => r.color))
      return this.map_colors.find(c => !used.has(c)) || this.map_colors[0]
    },
  },
  methods: {
    ...mapActions('tazApi', [
      'getFacilitiesForMap',
      'getRepListByZipCodeReport',
      'getTerrZipGeoJson',
      'loadSavedMap',
    ]),
    async loadSavedMapData () {
      this.savedTerrRepZipsRpt = await this.loadSavedMap(this.id)
      this.paras = this.savedTerrRepZipsRpt.paras
      await this.onGetTerrZipGeoJson()
      await this.onSearchFacilities()
      for (let trzIdx = 0; trzIdx < this.savedTerrRepZipsRpt.jsonData.length; trzIdx++) {
        const trz = this.savedTerrRepZipsRpt.jsonData[trzIdx]
        for (let zipIdx = 0; zipIdx < trz.zips.length; zipIdx++) {
          const zipData = trz.zips[zipIdx]
          const geoJsonZip = this.getGeoJsonByZip(zipData.zip)
          if (geoJsonZip) {
            const zipProps = geoJsonZip.properties
            zipProps.terr = trz.territoryId
            zipProps.rep = trz.repName
            zipProps.terrRepId = trz.territoryXRepId
            zipProps.color = trz.color
            zipProps.changed = zipData.changed
          }
        }
      }
      this.tazTerrXRepsXZips = this.savedTerrRepZipsRpt.jsonData
      this.generateMap()
    },
    getGeoJsonByZip (zip) {
      if (!this.zipGeoJsons || !this.zipGeoJsons.features) return null
      return this.zipGeoJsons.features.find(p => p.properties.ZCTA5CE10 === zip) || null
    },
    async onGetTerrZipGeoJson () {
      const rptParas = { rptType: 'html', state: this.paras.state, terrType: this.paras.territoryTypeInMap }
      this.zipGeoJsons = await this.getTerrZipGeoJson(rptParas)
      if (this.zipGeoJsons == null) {
        this.$router.push('/')
      }
    },
    onSubmit () {
      if (parseZips(this.zipsSelected).length > 0) {
        if (!confirm('You have pending ZIPs that have not been reassigned. Loading a new state will discard them. Continue?')) {
          return
        }
        this.zipsSelected = ''
      }
      return this.onPopulateMap()
    },
    async onPopulateMap () {
      await this.onGetTerrZipGeoJson()
      await this.onSearchFacilities()
      this.generateMap()
      this.populateTerrRepZipEditData()
    },
    async onSearchFacilities () {
      const rpt = await this.getFacilitiesForMap(this.paras)
      this.locations = rpt.markers
      this.territories = rpt.territories
    },
    populateTerrRepZipEditData () {
      this.tazTerrXRepsXZips = []
      const terrReps = new Map()
      if (!this.zipGeoJsons.features) return terrReps
      for (const zipGeoJson of this.zipGeoJsons.features) {
        const terrRepId = zipGeoJson.properties.terrRepId
        if (!terrRepId) continue
        if (!terrReps.has(terrRepId)) {
          terrReps.set(terrRepId, {
            territoryXRepId: zipGeoJson.properties.terrRepId,
            territoryId: zipGeoJson.properties.terr,
            repName: zipGeoJson.properties.rep,
            color: zipGeoJson.properties.color,
            zips: [],
            terrRepSalesAmt: 0,
          })
        }
        const terrRep = terrReps.get(terrRepId)
        terrRep.zips.push({
          zip: zipGeoJson.properties.ZCTA5CE10,
          salesAmt: zipGeoJson.properties.salesAmt,
          changed: zipGeoJson.properties.changed,
        })
        if (zipGeoJson.properties.salesAmt) terrRep.terrRepSalesAmt += zipGeoJson.properties.salesAmt
      }
      this.tazTerrXRepsXZips = [...terrReps.values()]
      for (const trz of this.tazTerrXRepsXZips) {
        trz.zips = trz.zips.sort((a, b) => a.zip > b.zip ? 1 : a.zip < b.zip ? -1 : 0)
      }
    },
    onAssignZips (trz) {
      const zips = parseZips(this.zipsSelected)
      if (zips.length === 0) return
      for (const z of zips) {
        const geoJsonZip = this.getGeoJsonByZip(z.replace(/\s+/g, ''))
        if (geoJsonZip) {
          const zipProps = geoJsonZip.properties
          zipProps.terr = trz.territoryId
          zipProps.rep = trz.repName
          zipProps.terrRepId = trz.territoryXRepId
          zipProps.color = trz.color
          zipProps.changed = true
        }
      }
      this.zipsSelected = ''
      this.applyReassignStyles()
      this.populateTerrRepZipEditData()
    },
    // Repaint the map fill colors after reassignment without rebuilding the whole map.
    // Iterates each feature and applies an overrideStyle reflecting its (possibly new) color.
    applyReassignStyles () {
      if (!this._map || !this._map.data) return
      this._map.data.forEach((feature) => {
        const zipColor = feature.getProperty('color')
        this._map.data.overrideStyle(feature, { fillColor: zipColor || 'lightblue' })
      })
    },
    onHighlightRep (repId) {
      if (this.highlightTimer) clearTimeout(this.highlightTimer)
      this.highlightedRepId = repId
      this.applyHighlight()
      this.highlightTimer = setTimeout(() => {
        this.highlightedRepId = null
        this.applyHighlight()
        this.highlightTimer = null
      }, 3000)
    },
    applyHighlight () {
      if (!this._map || !this._map.data) return
      this._map.data.revertStyle()
      if (this.highlightedRepId) {
        this._map.data.forEach((feature) => {
          if (feature.getProperty('terrRepId') === this.highlightedRepId) {
            this._map.data.overrideStyle(feature, { strokeWeight: 3, strokeColor: '#000' })
          }
        })
      }
    },
    // Flash the ZIPs currently staged in the Pending tray on the map (3-second
    // thicker stroke). Reuses the same timer slot as onHighlightRep so the two
    // highlight modes can't double-fire.
    onHighlightPending () {
      if (!this._map || !this._map.data) return
      const zips = new Set(parseZips(this.zipsSelected))
      if (zips.size === 0) return
      if (this.highlightTimer) clearTimeout(this.highlightTimer)
      this.highlightedRepId = null
      this._map.data.revertStyle()
      this._map.data.forEach((feature) => {
        if (zips.has(feature.getProperty('ZCTA5CE10'))) {
          this._map.data.overrideStyle(feature, { strokeWeight: 3, strokeColor: '#000' })
        }
      })
      this.highlightTimer = setTimeout(() => {
        if (this._map && this._map.data) this._map.data.revertStyle()
        this.highlightTimer = null
      }, 3000)
    },
    onAddNewRep (form) {
      this.tazTerrXRepsXZips.push({
        territoryId: form.territoryId,
        repName: form.repName,
        territoryXRepId: this.tazTerrXRepsXZips.length, // matches today's onAddNewZipAssignment behavior
        zips: [],
        color: form.color,
        terrRepSalesAmt: 0,
      })
    },
    onShowSaveTerrRepZipsRpt () {
      this.savedTerrRepZipsRpt = Object.assign(this.savedTerrRepZipsRpt, {
        jsonData: this.tazTerrXRepsXZips,
        updateBy: this.userData ? this.userData.fullName : '',
        active: true,
        paras: this.paras,
      })
      this.showSaveTerrRepZipsRpt = true
    },
    async generateMap () {
      try {
        const google = await gmapsInit()
        const divmap = document.getElementById('zacDivmap')
        const map = new google.maps.Map(divmap)
        this._map = map
        const bounds = new google.maps.LatLngBounds()
        const boundsCoordinates = collectMapBoundsCoordinates(this.locations, this.zipGeoJsons)
        for (let i = 0; i < boundsCoordinates.length; i++) bounds.extend(boundsCoordinates[i])
        if (boundsCoordinates.length > 0) map.fitBounds(bounds)
        this.locations.forEach((location) => {
          const marker = new google.maps.Marker({ ...location, map })
          marker.infowindow = new google.maps.InfoWindow({
            content: `<div><b>${location.metaData.code} of ${location.metaData.territory}</b></div>` +
                     `<div>${location.title}</div><div>${location.metaData.address}</div>` +
                     `<div>$${location.metaData.salesAmt ? location.metaData.salesAmt.toFixed(2) : 0}</div>`,
          })
          marker.addListener('mouseover', function () { this.infowindow.open(map, this) })
          marker.addListener('mouseout', function () { this.infowindow.close() })
        })
        const that = this
        map.data.setStyle(function (feature) {
          const zipColor = feature.getProperty('color')
          return { fillColor: zipColor || 'lightblue', strokeWeight: 0.5, strokeColor: '#444' }
        })
        map.data.addGeoJson(this.zipGeoJsons)
        // Static hover panel: write the hovered feature's data to the component;
        // the template renders it in a fixed bar at the top of the map area.
        map.data.addListener('mouseover', function (event) {
          that.hoveredFeature = {
            zip: getFeatureProperty(event.feature, 'ZCTA5CE10'),
            terr: getFeatureProperty(event.feature, 'terr'),
            rep: getFeatureProperty(event.feature, 'rep'),
            salesAmt: getFeatureProperty(event.feature, 'salesAmt'),
          }
        })
        map.data.addListener('mouseout', function () { that.hoveredFeature = null })
        // Click adds the ZIP to the pending tray. parseZips dedupes (#6) and
        // re-joins with proper ", " separators so a paste like "48188" + click
        // produces "48188, 48105" instead of "4818848105" (#7).
        map.data.addListener('click', function (event) {
          const zip = getFeatureProperty(event.feature, 'ZCTA5CE10')
          if (!zip) return
          const existing = parseZips(that.zipsSelected)
          if (existing.includes(zip)) return
          existing.push(zip)
          that.zipsSelected = existing.join(', ')
          map.data.overrideStyle(event.feature, { fillColor: 'grey' })
        })
        map.data.addListener('addfeature', function (evt) {
          const zip = getFeatureProperty(evt.feature, 'ZCTA5CE10')
          if (zip) {
            const coord = {
              lng: parseFloat(getFeatureProperty(evt.feature, 'INTPTLON10')),
              lat: parseFloat(getFeatureProperty(evt.feature, 'INTPTLAT10')),
            }
            if (Number.isFinite(coord.lng) && Number.isFinite(coord.lat)) {
              bounds.extend(coord)
              map.fitBounds(bounds)
            }
          }
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    },
  },
  beforeDestroy () {
    if (this.highlightTimer) clearTimeout(this.highlightTimer)
  },
  async mounted () {
    this.showInstructions = true
    this.usStates = this.allUsStates()
    if (!this.isNew) {
      await this.loadSavedMapData()
    }
    this.map_colors = [
      '#00FFFF','#DA70D6','#8B0000','#FA8072','#1E90FF','#00FF00','#FFA07A','#E9967A',
      '#F08080','#CD5C5C','#DC143C','#B22222','#FF0000','#FF7F50','#FF6347','#FF4500',
      '#FFD700','#FFA500','#FF8C00','#EEE8AA','#F0E68C','#BDB76B','#FFFF00','#7CFC00',
      '#7FFF00','#32CD32','#228B22','#008000','#006400','#ADFF2F','#9ACD32','#00FF7F',
      '#00FA9A','#90EE90','#98FB98','#8FBC8F','#3CB371','#2E8B57','#808000','#556B2F',
      '#6B8E23','#E6E6FA','#D8BFD8','#DDA0DD','#EE82EE','#FF00FF','#BA55D3','#9370DB',
      '#8A2BE2','#9400D3','#9932CC','#8B008B','#800080','#4B0082','#FFC0CB','#FFB6C1',
      '#FF69B4','#FF1493','#DB7093','#C71585',
    ]
  },
}
</script>

<style scoped>
.zac-root      { display: flex; flex-direction: column; height: 100vh; }
.zac-toolbar   { flex: 0 0 auto; }
.zac-body      { flex: 1; display: flex; min-height: 0; }
.zac-map-area      { flex: 1; min-width: 0; position: relative; background: #f5f5f5; display: flex; align-items: center; justify-content: center; }
.zac-sidebar       { width: 340px; border-left: 1px solid #ddd; background: #fafafa; overflow-y: auto; }
.zac-toolbar-field { width: 160px; margin-right: 8px; }
.zac-map           { width: 100%; height: 100%; }
.zac-empty         { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); color: #777; font-size: 14px; pointer-events: none; }
.zac-legend-btn  { position: absolute; bottom: 16px; left: 16px; z-index: 2; }
.zac-legend      { max-width: 240px; }
.zac-legend-icon { width: 18px; height: 18px; vertical-align: middle; }
.zac-sidebar              { transition: width 0.2s ease; }
.zac-sidebar--collapsed   { width: 0 !important; border-left: none; overflow: hidden; }
.zac-hoverbar {
  position: absolute; top: 0; left: 0; right: 0; z-index: 2;
  background: rgba(255, 255, 255, 0.96);
  border-bottom: 1px solid #ddd;
  padding: 6px 16px;
  display: flex; gap: 24px; flex-wrap: wrap;
  font-size: 13px;
  pointer-events: none;
}
.zac-hoverbar b { font-weight: 600; color: #555; margin-right: 4px; }
.zac-howto       { padding-left: 20px; line-height: 1.7; }
.zac-howto li    { margin-bottom: 4px; }
</style>
